import { useQuery } from "@tanstack/react-query";
import { useAppRouter } from "~/hooks/use-app-router";

import { apiGqlClient } from "~/libs/gql";
import { graphql } from "~/types/__generated/gql";
import { GetFinanceReportsQuery } from "~/types/__generated/gql/graphql";

const query = /* GraphQL */ `
	query GetFinanceReports($from: String!, $to: String!) {
		getFinanceReports(from: $from, to: $to) {
			_id
			type
			createdAt

			_accountTransactions {
				_id
				reference {
					number
				}
				paymentVerification
				_paymentVerification {
					_id
					createdAt
					voidedAt
					transactionId
					reference {
						number
					}
					receivedAmount
					receivedDate
					paymentStage
					paymentPurpose
					receivedStatus
					currency
					remarks
					attachment {
						name
						size
						url
						extension
					}
				}
			}

			files
			_files {
				_id
				createdAt
				updatedAt
				isProcessed
				products {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					multiCurrencyConfig {
						selectedCurrency
						priceConfigs {
							code
							price
							rate
							isPrimaryCurrency
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						multiCurrencyConfig {
							selectedCurrency
							priceConfigs {
								code
								price
								rate
								isPrimaryCurrency
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				cancelledProducts {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					multiCurrencyConfig {
						selectedCurrency
						priceConfigs {
							code
							price
							rate
							isPrimaryCurrency
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						multiCurrencyConfig {
							selectedCurrency
							priceConfigs {
								code
								price
								rate
								isPrimaryCurrency
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				process
				company
				user
				client
				agent
				sortingIndex
				assignees
				processPaths {
					process
					leadTime
					proccessRemovalDuration

					_process {
						_id
						name
					}
				}
				processLastUpdatedAt
				customFields {
					customField
					textValue
					selectValues
					dateTimeValue

					_customField {
						name
						type
					}
				}
				counterConfigs {
					file {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					invoice {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					monthlyStatement {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					task {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
				}
				multiCurrencyConfig {
					selectedCurrency
					currencyConfigs {
						code
						rate
						isPrimaryCurrency
					}
				}
				receipt
				paymentVerifications
				fileSuffixComment
				receiptSuffixComment
				issueDate
				dueDate
				isCancelled
				receiptNote
				taskStatus

				_process {
					_id
					createdAt
					updatedAt
					name
				}
				_user {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
				_client {
					_id
					createdAt
					updatedAt
					name
					addresses {
						title
						country
						province
						city
						area
						street
						building
						unit
						floor
					}
					contacts {
						firstName
						lastName
						email
						telephone
						mobile
						gender
						position
					}
				}
				_agent {
					_id
					createdAt
					updatedAt
					name
					_company {
						name
						address
					}
					contacts {
						firstName
						lastName
						mobile
					}
				}
				_assignees {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
				_receipt {
					_id
					createdAt
					updatedAt
					company
					user
					isFullyPaid
					payments {
						paidAt
						voidedAt
						transactionId
						paymentVerificationId
						paymentStage
						payments {
							amount
							voidedAmount
							payment_type
							bank_received_date
							multiCurrencyConfig {
								selectedCurrency
								currencyConfigs {
									code
									rate
									isPrimaryCurrency
								}
							}
						}
					}
				}
				_paymentVerifications {
					_id
					createdAt
					updatedAt
					voidedAt
					transactionId
					user
					files
					attachment {
						name
						size
						url
						extension
					}
					reference {
						counter
						number

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					status
					receivedAmount
					receivedDate
					paymentStage
					paymentPurpose
					receivedStatus
					currency
					remarks

					counterConfig {
						file {
							counter
							index
							increment

							_counter {
								_id
								type
								prefix
								suffix_digit
								isPredefinedFormat
							}
						}
					}

					_transaction {
						_id
						_account {
							currency
							accountId
						}
						_agents {
							name
						}
						_clients {
							name
						}
					}
				}
			}
			counterConfigs {
				monthlyStatement {
					counter
					index
					reference

					_counter {
						_id
						type
						prefix
						suffix_digit
						isPredefinedFormat
					}
				}
			}
			issueDate
			dueDate
		}
	}
`;

export type GetFinanceReportsResults =
	GetFinanceReportsQuery["getFinanceReports"];

export const getFinanceReportsQueryKey = () => ["getFinanceReports"];

export const useGetFinanceReports = (enabled?: boolean) => {
	const { appQuery } = useAppRouter();
	return useQuery({
		enabled: enabled ?? true,
		queryKey: getFinanceReportsQueryKey(),
		queryFn: async () =>
			apiGqlClient
				.request(graphql(query), {
					from: appQuery.from ?? "",
					to: appQuery.to ?? "",
				})
				.then((res) => res.getFinanceReports),
	});
};

export const useGetFinanceReportsCache = () => {
	return useGetFinanceReports(false);
};
